import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Row, Col, Card, Badge, Spinner, Button, Table } from 'react-bootstrap';

export default function ProposicaoDetalhes() {
    const [searchParams] = useSearchParams();
    const [proposicao, setProposicao] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [dadosTecnicos, setDadosTecnicos] = useState(null);
    const [dadosHistorico, setDadosHistorico] = useState(null);

    // Suporte aos novos parâmetros docid/tipoprop e aos antigos para compatibilidade
    const docid = searchParams.get('docid');
    const tipoprop = searchParams.get('tipoprop');
    const tipo = searchParams.get('tipo');
    const numero = searchParams.get('numero');
    const ano = searchParams.get('ano');
    
    // Mapeamento dos códigos tipoprop para tipos completos
    const tipoPropMapping = {
        'p': 'projeto',
        'i': 'indicacao',
        'pc': 'parecer',
        'r': 'requerimento'
    };
    
    // Mapeamento inverso para tipoprop
    const tipoToTipoProp = {
        'projeto': 'p',
        'indicacao': 'i', 
        'parecer': 'pc',
        'requerimento': 'r'
    };

    useEffect(() => {
        // Verifica se temos os novos parâmetros ou os antigos
        if (docid && tipoprop) {
            buscarDetalhes();
        } else if (tipo && numero && ano) {
            buscarDetalhes();
        } else {
            setError('Parâmetros inválidos para consulta. Use docid/tipoprop ou tipo/numero/ano');
            setLoading(false);
        }
    }, [docid, tipoprop, tipo, numero, ano]);

    const buscarDetalhes = async () => {
        setLoading(true);
        setError('');

        try {
            let url;
            
            if (docid && tipoprop) {
                // Usar novo sistema docid/tipoprop
                url = `https://dadosabertos.alepe.pe.gov.br/api/v1/proposicoes/projetos/?docid=${docid}&tipoprop=${tipoprop}`;
            } else {
                // Usar sistema legado numero/ano/tipo
                const tipoPlural = tipo + 's'; // Convert to plural for API
                url = `https://dadosabertos.alepe.pe.gov.br/api/v1/proposicoes/${tipoPlural}/?numero=${numero}&ano=${ano}`;
            }
            
            const response = await fetch(url);
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            const xmlData = await response.text();
            
            // Parse the detailed XML
            const dadosDetalhados = extrairDadosDetalhadosCompletos(xmlData);
            
            // Determinar o tipo baseado nos parâmetros
            let tipoFinal;
            if (docid && tipoprop) {
                tipoFinal = tipoPropMapping[tipoprop] || tipoprop;
            } else {
                tipoFinal = tipo;
            }
            
            setProposicao({
                xml: xmlData,
                tipo: tipoFinal,
                dados: dadosDetalhados
            });
            
            // Extract all detailed data and separate them
            const todosOsDados = extrairDadosDetalhados(xmlData);
            setDadosTecnicos(separaDadosTecnicos(todosOsDados));
            setDadosHistorico(separaDadosHistorico(todosOsDados));
            
        } catch (error) {
            console.error('Erro ao carregar detalhes:', error);
            setError(`Erro ao carregar detalhes: ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    const extrairDadosDetalhadosCompletos = (xmlString) => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(xmlString, 'text/xml');
        
        const mainElement = doc.querySelector('projeto') || doc.querySelector('indicacao') || doc.querySelector('requerimento');
        
        let dados = {
            numero: 'N/A',
            ano: 'N/A',
            ementa: 'Ementa não disponível',
            situacao: 'N/A',
            autor: 'N/A',
            autores: [],
            dataCriacao: 'N/A',
            tipo: 'N/A',
            subtipo: 'N/A',
            legislatura: 'N/A',
            materia: 'N/A',
            justificativa: 'N/A',
            regimeTramitacao: 'N/A',
            impactoOrcamentario: 'N/A',
            resultadoFinal: 'N/A',
            numeroDpl: 'N/A',
            dataPublicacao: 'N/A',
            lotacaoAtual: 'N/A',
            primeiraInsercaoOd: 'N/A'
        };
        
        if (mainElement) {
            // Basic attributes
            dados.numero = mainElement.getAttribute('numero') || 'N/A';
            dados.ano = mainElement.getAttribute('ano') || 'N/A';
            dados.tipo = mainElement.getAttribute('tipo') || 'N/A';
            dados.subtipo = mainElement.getAttribute('subtipo') || 'N/A';
            dados.legislatura = mainElement.getAttribute('legislatura') || 'N/A';
            dados.situacao = mainElement.getAttribute('situacao') || 'Tramitando';
            
            // Extract all authors
            const autorElements = mainElement.querySelectorAll('autores autor');
            dados.autores = Array.from(autorElements).map(autor => ({
                nome: autor.getAttribute('nome') || 'N/A',
                tipo: autor.getAttribute('tipo') || 'N/A'
            }));
            
            // Main author for backward compatibility
            if (dados.autores.length > 0) {
                dados.autor = dados.autores[0].nome;
            }
            
            // Extract detailed content
            dados.ementa = getElementText(doc, 'ementa') || 'Ementa não disponível';
            dados.materia = getElementText(doc, 'materia') || 'N/A';
            dados.justificativa = getElementText(doc, 'justificativa') || 'N/A';
            dados.regimeTramitacao = getElementText(doc, 'regimeTramitacao') || 'N/A';
            dados.impactoOrcamentario = getElementText(doc, 'impactoOrcamentario') || 'N/A';
            dados.resultadoFinal = getElementText(doc, 'resultadoFinal') || 'N/A';
            dados.numeroDpl = getElementText(doc, 'numeroDpl') || 'N/A';
            dados.dataPublicacao = getElementText(doc, 'dataPublicacao') || 'N/A';
            dados.lotacaoAtual = getElementText(doc, 'lotacaoAtual') || 'N/A';
            
            // Extract creation date from datas section
            const dataElements = doc.querySelectorAll('datas data');
            for (let dataEl of dataElements) {
                if (dataEl.getAttribute('tipo') === 'criacao') {
                    dados.dataCriacao = dataEl.textContent.trim();
                    break;
                }
            }
            
            if (dados.dataCriacao === 'N/A') {
                dados.dataCriacao = getElementText(doc, 'dataApresentacao') ||
                                  getElementText(doc, 'data') ||
                                  'N/A';
            }
        }
        
        return dados;
    };

    const getElementText = (doc, tagName) => {
        const element = doc.querySelector(tagName);
        return element ? element.textContent.trim() : null;
    };

    const extrairDadosDetalhados = (xmlString) => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(xmlString, 'text/xml');
        
        const dados = {
            datas: [],
            historico: [],
            distribuicao: []
        };
        
        // Extract dates
        const dataElements = doc.querySelectorAll('datas data');
        for (let dataEl of dataElements) {
            dados.datas.push({
                tipo: dataEl.getAttribute('tipo') || 'N/A',
                data: dataEl.textContent.trim()
            });
        }
        
        // Extract historical events
        const eventoElements = doc.querySelectorAll('historico evento');
        for (let evento of eventoElements) {
            dados.historico.push({
                data: evento.getAttribute('data') || 'N/A',
                acao: evento.textContent.trim()
            });
        }
        
        // Extract distribution information
        const distribElements = doc.querySelectorAll('distribuicao distribuido');
        for (let distrib of distribElements) {
            dados.distribuicao.push({
                comissao: distrib.getAttribute('comissao') || 'N/A',
                data: distrib.getAttribute('data') || 'N/A'
            });
        }
        
        return dados;
    };

    const separaDadosTecnicos = (dados) => {
        if (!dados) return null;
        
        const camposTecnicos = ['materia', 'justificativa', 'regime tramitacao', 'impacto orcamentario'];
        const dadosTecnicos = {};
        
        Object.keys(dados).forEach(key => {
            const keyLower = key.toLowerCase();
            if (camposTecnicos.some(campo => keyLower.includes(campo))) {
                dadosTecnicos[key] = dados[key];
            }
        });
        
        return Object.keys(dadosTecnicos).length > 0 ? dadosTecnicos : null;
    };

    const separaDadosHistorico = (dados) => {
        if (!dados) return null;
        
        const camposHistorico = ['datas', 'historico', 'resultado final', 'situacao atual', 'tramitacao'];
        const dadosHistorico = {};
        
        Object.keys(dados).forEach(key => {
            const keyLower = key.toLowerCase();
            if (camposHistorico.some(campo => keyLower.includes(campo))) {
                dadosHistorico[key] = dados[key];
            }
        });
        
        return Object.keys(dadosHistorico).length > 0 ? dadosHistorico : null;
    };

    const formatarChave = (chave) => {
        return chave
            .replace(/([A-Z])/g, ' $1')
            .replace(/_/g, ' ')
            .toLowerCase()
            .replace(/^\w/, c => c.toUpperCase());
    };

    const formatarData = (data) => {
        if (!data || data === 'N/A') return 'N/A';
        
        const formatos = [
            /^(\d{4})-(\d{2})-(\d{2})/,
            /^(\d{2})\/(\d{2})\/(\d{4})/,
            /^(\d{4})\/(\d{2})\/(\d{2})/
        ];
        
        for (let formato of formatos) {
            const match = data.match(formato);
            if (match) {
                let dia, mes, ano;
                if (formato.source.startsWith('^(\\d{4})')) {
                    ano = match[1];
                    mes = match[2];
                    dia = match[3];
                } else {
                    dia = match[1];
                    mes = match[2];
                    ano = match[3];
                }
                return `${dia}/${mes}/${ano}`;
            }
        }
        
        return data;
    };

    const getTipoNome = (tipo) => {
        const nomes = {
            'projeto': 'Projeto de Lei',
            'indicacao': 'Indicação',
            'requerimento': 'Requerimento'
        };
        return nomes[tipo] || tipo.charAt(0).toUpperCase() + tipo.slice(1);
    };

    const getTipoBadgeClass = (tipo) => {
        const classes = {
            'projeto': 'success',
            'indicacao': 'info',
            'requerimento': 'warning'
        };
        return classes[tipo] || 'secondary';
    };

    const copiarDetalhes = async () => {
        if (!proposicao) return;
        
        const texto = `${getTipoNome(proposicao.tipo)} ${proposicao.dados.numero}/${proposicao.dados.ano}\n\n` +
            `Autor: ${proposicao.dados.autor}\n` +
            `Data: ${formatarData(proposicao.dados.dataCriacao)}\n` +
            `Situação: ${proposicao.dados.situacao}\n\n` +
            `Ementa:\n${proposicao.dados.ementa}`;
        
        try {
            await navigator.clipboard.writeText(texto);
            alert('Detalhes copiados para a área de transferência!');
        } catch (err) {
            console.error('Erro ao copiar:', err);
            alert('Erro ao copiar detalhes.');
        }
    };

    const historicoComDatas = () => {
        if (!dadosHistorico || !dadosHistorico['datas'] || !dadosHistorico['historico']) {
            return [];
        }
        
        const datasTexto = dadosHistorico['datas'];
        const datas = datasTexto.match(/\d{2}\/\d{2}\/\d{4}/g) || [];
        
        const historicoTexto = dadosHistorico['historico'];
        const statusList = [];
        
        const statusPatterns = [
            'AGUARDANDO APOIAMENTO',
            'ENVIADO P/ SGMD',
            'ALTERAÇÃO DE COAUTOR',
            'NUMERADO',
            'DESPACHADO',
            'EMITIR PARECER',
            'ENVIADO PARA PUBLICAÇÃO',
            'PUBLICADO',
            'TRAMITANDO',
            'APROVADO',
            'REJEITADO',
            'ARQUIVADO'
        ];
        
        let remainingText = historicoTexto;
        
        statusPatterns.forEach(pattern => {
            if (remainingText.includes(pattern)) {
                statusList.push(pattern);
                remainingText = remainingText.replace(pattern, '|').replace(/\|+/g, '|');
            }
        });
        
        if (statusList.length === 0) {
            const matches = historicoTexto.match(/[A-Z][A-Z\s]*(?=[A-Z][A-Z\s]*|$)/g) || [];
            statusList.push(...matches.map(m => m.trim()).filter(m => m.length > 1));
        }
        
        const timeline = [];
        const maxLength = Math.max(datas.length, statusList.length);
        
        for (let i = 0; i < maxLength; i++) {
            const data = datas[i] || null;
            const status = statusList[i] || null;
            
            if (status && status.length > 1) {
                timeline.push({
                    data: data,
                    status: status
                });
            }
        }
        
        return timeline;
    };

    const dadosHistoricoSemTimeline = () => {
        if (!dadosHistorico) return {};
        
        const semTimeline = { ...dadosHistorico };
        delete semTimeline['datas'];
        delete semTimeline['historico'];
        
        return semTimeline;
    };

    if (loading) {
        return (
            <Row>
                <Col xs="12" className="text-center py-5">
                    <Spinner animation="border" role="status">
                        <span className="visually-hidden">Carregando...</span>
                    </Spinner>
                    <div className="mt-3">
                        <p className="text-muted">Carregando detalhes da proposição...</p>
                    </div>
                </Col>
            </Row>
        );
    }

    if (error) {
        return (
            <Row>
                <Col xs="12">
                    <div className="alert alert-danger">
                        <strong>Erro:</strong> {error}
                    </div>
                    <Link to="/proposicoes" className="btn btn-primary">
                        Voltar à Busca
                    </Link>
                </Col>
            </Row>
        );
    }

    if (!proposicao) {
        return (
            <Row>
                <Col xs="12">
                    <div className="alert alert-warning">
                        Proposição não encontrada.
                    </div>
                    <Link to="/proposicoes" className="btn btn-primary">
                        Voltar à Busca
                    </Link>
                </Col>
            </Row>
        );
    }

    return (
        <Row>
            <Col xs="12">
                <p>
                    <small>
                        <Link to="/proposicoes">Consulta de Proposições</Link> / {getTipoNome(proposicao.tipo)} {proposicao.dados.numero}/{proposicao.dados.ano}
                    </small>
                </p>
            </Col>
            
            <Col xs="12" className="text-center mb-4">
                <h2>{getTipoNome(proposicao.tipo)} {proposicao.dados.numero}/{proposicao.dados.ano}</h2>
                {proposicao.dados.subtipo && proposicao.dados.subtipo !== 'N/A' && (
                    <h2 style={{ fontSize: '80%' }}>{proposicao.dados.subtipo}</h2>
                )}
            </Col>

            {/* Informações Gerais */}
            <Col xs="12" className='mt-3'>
                <div className="proposicao-list-item-header"><h3>Informações Gerais</h3></div>
                <div className="proposicao-list-item-text">
                    <Table>
                        <tbody>
                            <tr><td><strong>Número/Ano:</strong></td><td>{proposicao.dados.numero}/{proposicao.dados.ano}</td></tr>
                            {proposicao.dados.autor !== 'N/A' && (
                                <tr><td><strong>Autor Principal:</strong></td><td>{proposicao.dados.autor}</td></tr>
                            )}
                            {proposicao.dados.dataCriacao !== 'N/A' && (
                                <tr><td><strong>Data de Criação:</strong></td><td>{formatarData(proposicao.dados.dataCriacao)}</td></tr>
                            )}
                            {proposicao.dados.situacao !== 'N/A' && (
                                <tr><td><strong>Situação:</strong></td><td>{proposicao.dados.situacao}</td></tr>
                            )}
                            {proposicao.dados.legislatura !== 'N/A' && (
                                <tr><td><strong>Legislatura:</strong></td><td>{proposicao.dados.legislatura}</td></tr>
                            )}
                            {proposicao.dados.regimeTramitacao !== 'N/A' && (
                                <tr><td><strong>Regime de Tramitação:</strong></td><td>{proposicao.dados.regimeTramitacao}</td></tr>
                            )}
                            {proposicao.dados.impactoOrcamentario !== 'N/A' && (
                                <tr><td><strong>Impacto Orçamentário:</strong></td><td>{proposicao.dados.impactoOrcamentario}</td></tr>
                            )}
                            {proposicao.dados.resultadoFinal !== 'N/A' && (
                                <tr><td><strong>Resultado Final:</strong></td><td>{proposicao.dados.resultadoFinal}</td></tr>
                            )}
                            {proposicao.dados.numeroDpl !== 'N/A' && (
                                <tr><td><strong>D.P.L.:</strong></td><td>{proposicao.dados.numeroDpl}</td></tr>
                            )}
                            {proposicao.dados.dataPublicacao !== 'N/A' && (
                                <tr><td><strong>Data de Publicação:</strong></td><td>{formatarData(proposicao.dados.dataPublicacao)}</td></tr>
                            )}
                            {proposicao.dados.lotacaoAtual !== 'N/A' && (
                                <tr><td><strong>Localização:</strong></td><td>{proposicao.dados.lotacaoAtual}</td></tr>
                            )}
                        </tbody>
                    </Table>
                </div>
            </Col>

            {/* Autores */}
            {proposicao.dados.autores && proposicao.dados.autores.length > 0 && (
                <Col xs="12" className='mt-3'>
                    <div className="proposicao-list-item-header"><h3>Autores</h3></div>
                    <div className="proposicao-list-item-text">
                        <Table>
                            <thead>
                                <tr>
                                    <th>Nome</th>
                                    <th>Tipo</th>
                                </tr>
                            </thead>
                            <tbody>
                                {proposicao.dados.autores.map((autor, index) => (
                                    <tr key={index}>
                                        <td>{autor.nome}</td>
                                        <td>{autor.tipo}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                </Col>
            )}

            {/* Ementa */}
            <Col xs="12" className='mt-3'>
                <div className="proposicao-list-item-header"><h3>Ementa</h3></div>
                <div className="proposicao-list-item-text">
                    <p style={{ textAlign: 'justify', whiteSpace: 'pre-wrap' }}>
                        {proposicao.dados.ementa}
                    </p>
                </div>
            </Col>

            {/* Matéria */}
            {proposicao.dados.materia !== 'N/A' && (
                <Col xs="12" className='mt-3'>
                    <div className="proposicao-list-item-header"><h3>Matéria/Texto Completo</h3></div>
                    <div className="proposicao-list-item-text">
                        <p style={{ textAlign: 'justify', whiteSpace: 'pre-wrap' }}>
                            {proposicao.dados.materia}
                        </p>
                    </div>
                </Col>
            )}

            {/* Justificativa */}
            {proposicao.dados.justificativa !== 'N/A' && (
                <Col xs="12" className='mt-3'>
                    <div className="proposicao-list-item-header"><h3>Justificativa</h3></div>
                    <div className="proposicao-list-item-text">
                        <p style={{ textAlign: 'justify', whiteSpace: 'pre-wrap' }}>
                            {proposicao.dados.justificativa}
                        </p>
                    </div>
                </Col>
            )}


            {/* Datas Importantes */}
            {dadosHistorico?.datas && dadosHistorico.datas.length > 0 && (
                <Col xs="12" className='mt-3'>
                    <div className="proposicao-list-item-header"><h3>Datas Importantes</h3></div>
                    <div className="proposicao-list-item-text">
                        <Table>
                            <thead>
                                <tr>
                                    <th>Tipo</th>
                                    <th>Data</th>
                                </tr>
                            </thead>
                            <tbody>
                                {dadosHistorico.datas.map((item, index) => (
                                    <tr key={index}>
                                        <td>{formatarChave(item.tipo)}</td>
                                        <td>{formatarData(item.data)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                </Col>
            )}

            {/* Histórico de Tramitação */}
            {dadosHistorico?.historico && dadosHistorico.historico.length > 0 && (
                <Col xs="12" className='mt-3'>
                    <div className="proposicao-list-item-header"><h3>Histórico de Tramitação</h3></div>
                    <div className="proposicao-list-item-text">
                        <Table>
                            <thead>
                                <tr>
                                    <th>Data</th>
                                    <th>Ação</th>
                                </tr>
                            </thead>
                            <tbody>
                                {dadosHistorico.historico.map((item, index) => (
                                    <tr key={index}>
                                        <td>{formatarData(item.data)}</td>
                                        <td>{item.acao}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                </Col>
            )}

            {/* Distribuição para Comissões */}
            {dadosHistorico?.distribuicao && dadosHistorico.distribuicao.length > 0 && (
                <Col xs="12" className='mt-3'>
                    <div className="proposicao-list-item-header"><h3>Distribuição para Comissões</h3></div>
                    <div className="proposicao-list-item-text">
                        <Table>
                            <thead>
                                <tr>
                                    <th>Comissão</th>
                                    <th>Data</th>
                                </tr>
                            </thead>
                            <tbody>
                                {dadosHistorico.distribuicao.map((item, index) => (
                                    <tr key={index}>
                                        <td>{item.comissao}</td>
                                        <td>{formatarData(item.data)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                </Col>
            )}

            {/* Ações */}
            <Col xs="12" className='mt-5 mb-4 text-center'>
                <Link to="/proposicoes" className="btn btn-primary me-3">
                    Voltar à Busca
                </Link>
                <button className="btn btn-outline-secondary" onClick={copiarDetalhes}>
                    Copiar Detalhes
                </button>
            </Col>
        </Row>
    );
}